import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import {
  ArrowPathIcon,
  DocumentArrowDownIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  BugAntIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import logApi from '../../../../services/logApi';
import { ApplicationLog, LogFilter, LogResponse, LogLevel, LOG_LEVEL_COLORS, LOG_LEVEL_BG_COLORS } from '../../../../types/ApplicationLog';
import LogDetails from './LogDetails';
import LogFilterPanel from './LogFilterPanel';
import LogStatistics from './LogStatistics';

const LogViewer: React.FC = () => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<ApplicationLog[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedLog, setSelectedLog] = useState<ApplicationLog | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [filter, setFilter] = useState<LogFilter>({
    pageSize: 50,
    pageNumber: 1,
  });

  const totalPages = Math.ceil(totalCount / filter.pageSize);

  useEffect(() => {
    fetchLogs();
  }, [filter]);

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      // Søk slått sammen med filtrene: én spørring som respekterer nivå/dato/operatør + fritekst
      const response: LogResponse = await logApi.getLogs({
        ...filter,
        searchTerm: searchTerm || undefined,
      });
      setLogs(response.logs);
      setTotalCount(response.totalCount);
    } catch (err) {
      setError(t('warehouse.modules.logs.errorFetchingLogs'));
      console.error('Error fetching logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setFilter({ ...filter, pageNumber: 1 });
    fetchLogs();
  };

  const handleFilterChange = (newFilter: Partial<LogFilter>) => {
    setFilter({ ...filter, ...newFilter, pageNumber: 1 });
  };

  const handleClearLogs = async () => {
    if (window.confirm(t('warehouse.modules.logs.confirmClearAll'))) {
      try {
        const result = await logApi.clearLogs();
        alert(`${t('warehouse.modules.logs.deleted')} ${result.deletedCount} ${t('warehouse.modules.logs.entries')}`);
        fetchLogs();
      } catch (err) {
        alert(t('warehouse.modules.logs.errorClearing'));
      }
    }
  };

  const handleExportCsv = async () => {
    try {
      const blob = await logApi.exportToCsv(filter.level, filter.fromDate, filter.toDate);
      logApi.downloadBlob(blob, `logs_${new Date().toISOString().split('T')[0]}.csv`);
    } catch (err) {
      alert(t('warehouse.modules.logs.errorExporting'));
    }
  };

  const handleExportExcel = async () => {
    try {
      const blob = await logApi.exportToExcel(filter.level, filter.fromDate, filter.toDate);
      logApi.downloadBlob(blob, `logs_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      alert(t('warehouse.modules.logs.errorExporting'));
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setFilter({ ...filter, pageNumber: newPage });
    }
  };

  const getLogIcon = (level: string) => {
    switch (level) {
      case 'Error':
      case 'Critical':
        return <XCircleIcon className="h-5 w-5" />;
      case 'Warning':
        return <ExclamationTriangleIcon className="h-5 w-5" />;
      case 'Debug':
        return <BugAntIcon className="h-5 w-5" />;
      default:
        return <InformationCircleIcon className="h-5 w-5" />;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const dateStr = date.toLocaleString('nb-NO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
    const ms = date.getMilliseconds().toString().padStart(3, '0');
    return `${dateStr}.${ms}`;
  };

  const truncateMessage = (message: string, maxLength: number = 150) => {
    return message.length > maxLength 
      ? `${message.substring(0, maxLength)}...` 
      : message;
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{t('warehouse.modules.logs.title')}</h1>
            <p className="text-sm text-gray-600 mt-1">
              {t('warehouse.modules.logs.subtitle')}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowStatistics(true)}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              title={t('warehouse.modules.logs.statistics')}
            >
              <ChartBarIcon className="h-5 w-5" />
            </button>
            <button
              onClick={fetchLogs}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              disabled={loading}
              title={t('common.buttons.refresh')}
            >
              <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <div className="h-6 w-px bg-gray-300" />
            <button
              onClick={handleExportCsv}
              className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              CSV
            </button>
            <button
              onClick={handleExportExcel}
              className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Excel
            </button>
            <button
              onClick={handleClearLogs}
              className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-1"
            >
              <TrashIcon className="h-4 w-4" />
              {t('common.buttons.clear')}
            </button>
          </div>
        </div>
      </div>

      {/* Filter Panel - alltid synlig, søk + filtre på én linje */}
      <LogFilterPanel
        filter={filter}
        onFilterChange={handleFilterChange}
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        onSearch={handleSearch}
      />

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {error && (
          <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">{t('common.status.loading')}</div>
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <DocumentArrowDownIcon className="h-12 w-12 mb-2" />
            <p>{t('warehouse.modules.logs.noLogs')}</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('warehouse.modules.logs.timestamp')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('warehouse.modules.logs.level')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('warehouse.modules.logs.message')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('warehouse.modules.logs.operator')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('warehouse.modules.logs.path')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('warehouse.modules.logs.version', {}, 'Versjon')}
                  </th>
                  <th className="px-2 py-3"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logs.map((log) => (
                  <tr
                    key={log.id}
                    className={`hover:bg-gray-50 cursor-pointer ${LOG_LEVEL_BG_COLORS[log.level as LogLevel] || ''}`}
                    onClick={() => {
                      setSelectedLog(log);
                      setShowDetails(true);
                    }}
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {formatTimestamp(log.timeStamp)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 text-sm font-medium ${LOG_LEVEL_COLORS[log.level as LogLevel] || 'text-gray-600'}`}>
                        {getLogIcon(log.level)}
                        {log.level}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div className="max-w-xl" title={log.message}>
                        {truncateMessage(log.message)}
                      </div>
                      {log.exception && (
                        <span className="text-red-600 text-xs">[Has exception]</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {log.operatorName || log.operatorId || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      <div className="max-w-xs truncate" title={log.requestPath}>
                        {log.requestPath || '-'}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-mono" title={log.version}>
                      {log.version || '-'}
                    </td>
                    <td className="px-2 py-3">
                      {log.correlationId && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Handle correlation ID click - show trace
                          }}
                          className="text-blue-600 hover:text-blue-800 text-xs"
                          title={`Trace: ${log.correlationId}`}
                        >
                          Trace
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="bg-white border-t border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            {t('warehouse.modules.logs.showing')} {((filter.pageNumber - 1) * filter.pageSize) + 1} - {Math.min(filter.pageNumber * filter.pageSize, totalCount)} {t('common.buttons.of')} {totalCount} {t('warehouse.modules.logs.entries')}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(filter.pageNumber - 1)}
              disabled={filter.pageNumber === 1}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <span className="px-3 py-1 text-sm">
              {t('warehouse.modules.logs.page')} {filter.pageNumber} {t('common.buttons.of')} {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(filter.pageNumber + 1)}
              disabled={filter.pageNumber === totalPages}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showDetails && selectedLog && (
        <LogDetails
          log={selectedLog}
          onClose={() => {
            setShowDetails(false);
            setSelectedLog(null);
          }}
        />
      )}

      {showStatistics && (
        <LogStatistics
          filter={filter}
          onClose={() => setShowStatistics(false)}
        />
      )}
    </div>
  );
};

export default LogViewer;